export const GOVERNOR_KEYS = Object.freeze({
  AX: 'AX',
  TREE: 'TREE',
  CALLS: 'CALLS',
  MESSAGING: 'MESSAGING',
  MEMBERSHIP: 'MEMBERSHIP',
  BUSINESS: 'BUSINESS',
  MEDIA: 'MEDIA',
  KINSSPACE: 'KINSSPACE',
  POLICY: 'POLICY',
  AI: 'AI',
} as const);

export type GovernorKey =
  typeof GOVERNOR_KEYS[keyof typeof GOVERNOR_KEYS];

export interface GovernorConstitutionalIdentity {
  readonly id: string;
  readonly key: GovernorKey;
  readonly governor: string;
  readonly domain: string;
}

export const GOVERNOR_IDENTITY_REGISTRY = Object.freeze({
  AX: Object.freeze({
    id: 'ax-governor',
    key: GOVERNOR_KEYS.AX,
    governor: 'AX Governor',
    domain: 'application-experience',
  }),
  TREE: Object.freeze({
    id: 'tree-governor',
    key: GOVERNOR_KEYS.TREE,
    governor: 'Tree Governor',
    domain: 'family-tree',
  }),
  CALLS: Object.freeze({
    id: 'calls-governor',
    key: GOVERNOR_KEYS.CALLS,
    governor: 'Calls Governor',
    domain: 'communications',
  }),
  MESSAGING: Object.freeze({
    id: 'messaging-governor',
    key: GOVERNOR_KEYS.MESSAGING,
    governor: 'Messaging Governor',
    domain: 'messaging',
  }),
  MEMBERSHIP: Object.freeze({
    id: 'membership-governor',
    key: GOVERNOR_KEYS.MEMBERSHIP,
    governor: 'Membership Governor',
    domain: 'membership',
  }),
  BUSINESS: Object.freeze({
    id: 'business-governor',
    key: GOVERNOR_KEYS.BUSINESS,
    governor: 'Business Governor',
    domain: 'business',
  }),
  MEDIA: Object.freeze({
    id: 'media-governor',
    key: GOVERNOR_KEYS.MEDIA,
    governor: 'Media Governor',
    domain: 'media',
  }),
  KINSSPACE: Object.freeze({
    id: 'kinsspace-governor',
    key: GOVERNOR_KEYS.KINSSPACE,
    governor: 'KinsSpace Governor',
    domain: 'kinsspace',
  }),
  POLICY: Object.freeze({
    id: 'policy-governor',
    key: GOVERNOR_KEYS.POLICY,
    governor: 'Policy Governor',
    domain: 'policy',
  }),
  AI: Object.freeze({
    id: 'ai-governor',
    key: GOVERNOR_KEYS.AI,
    governor: 'AI Governor',
    domain: 'artificial-intelligence',
  }),
} as const satisfies Record<
  GovernorKey,
  GovernorConstitutionalIdentity
>);

export const GOVERNOR_LIFECYCLE_IDENTIFIERS = Object.freeze({
  REGISTRATION: 'registration',
  ADAPTER: 'adapter',
  ACTIVATION: 'activation',
  AUTHORITY: 'authority',
  REGISTRY: 'registry',
  RUNTIME_VERIFICATION: 'runtime-verification',
  COMMAND_CENTER_FEED: 'command-center-feed',
  RUNTIME_DECISION: 'runtime-decision',
  RUNTIME_ACTION: 'runtime-action',
  RUNTIME_GUARD: 'runtime-guard',
  OPERATIONAL_CERTIFICATION: 'operational-certification',
} as const);

export type GovernorLifecycleIdentifier =
  typeof GOVERNOR_LIFECYCLE_IDENTIFIERS[
    keyof typeof GOVERNOR_LIFECYCLE_IDENTIFIERS
  ];

export function isGovernorKey(
  value: unknown,
): value is GovernorKey {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(
      GOVERNOR_IDENTITY_REGISTRY,
      value,
    )
  );
}

export function getGovernorConstitutionalIdentity<
  K extends GovernorKey,
>(
  key: K,
): (typeof GOVERNOR_IDENTITY_REGISTRY)[K] {
  if (!isGovernorKey(key)) {
    throw new Error(
      `Unknown Governor constitutional identity: ${String(key)}`,
    );
  }

  return GOVERNOR_IDENTITY_REGISTRY[key];
}
